import { MATRIX_LABELS } from '@crm/shared'
import type { CreateOrderPositionDto, MatrixLabel } from '@crm/shared'

/** Поля формы новой позиции — строки, как их вводит сотрудник. */
export interface NewPositionDraft {
  partNumber: string
  brand: string
  category: string
  quantity: string
  unit: string
  price: string
  matrix: '' | MatrixLabel
  supplierName: string
  costPrice: string
  costCurrency: 'RUB' | 'USD' | 'EUR' | 'CNY'
}

export const EMPTY_DRAFT: NewPositionDraft = {
  partNumber: '',
  brand: '',
  category: '',
  quantity: '1',
  unit: 'шт',
  price: '',
  matrix: '',
  supplierName: '',
  costPrice: '',
  costCurrency: 'RUB',
}

/** «1 234,56 ₽» → 1234.56; пусто или мусор → null. */
function parseNum(s: string): number | null {
  const t = s.replace(/[^\d.,-]/g, '').replace(',', '.')
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Черновик → тело запроса или текст ошибки для сотрудника. */
export function draftToDto(
  d: NewPositionDraft,
): { dto: Omit<CreateOrderPositionDto, 'invoiceId'> } | { error: string } {
  const partNumber = d.partNumber.trim()
  if (!partNumber) return { error: 'Укажите артикул' }
  const quantity = parseNum(d.quantity)
  if (quantity == null || quantity <= 0) return { error: 'Количество должно быть больше нуля' }
  const price = parseNum(d.price)
  if (price == null || price < 0) return { error: 'Укажите цену клиенту за единицу' }
  const costPrice = parseNum(d.costPrice)
  if (!d.matrix && (costPrice != null || d.supplierName.trim())) {
    return { error: 'Поставщик и себестоимость указываются вместе с матрицей' }
  }
  return {
    dto: {
      partNumber,
      brand: d.brand.trim() || null,
      category: d.category.trim() || null,
      quantity,
      unit: d.unit.trim() || null,
      price: Math.round(price * 10_000) / 10_000,
      matrix: d.matrix || null,
      supplierName: d.matrix ? d.supplierName.trim() || null : null,
      costPrice: d.matrix ? costPrice : null,
      costCurrency: d.matrix && costPrice != null ? d.costCurrency : null,
    },
  }
}

const input = 'w-full rounded border border-gray-300 px-2 py-1.5 text-sm'
const label = 'mb-1 block text-xs font-medium text-gray-600'

/**
 * Вкладка «Новая позиция» окна «Добавить позиции»: деталь, которой не было в
 * заявке. В заявке она появится сразу в «Счёте» с этой ценой клиенту. Матрица,
 * поставщик и себестоимость — по желанию (поставщик и себестоимость — только
 * вместе с матрицей: они записываются квотой в этой матрице).
 */
export function NewOrderPositionForm({
  draft,
  onChange,
}: {
  draft: NewPositionDraft
  onChange: (next: NewPositionDraft) => void
}) {
  const set = <K extends keyof NewPositionDraft>(key: K, value: NewPositionDraft[K]) =>
    onChange({ ...draft, [key]: value })

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label className="block sm:col-span-2">
        <span className={label}>Артикул *</span>
        <input
          className={input}
          value={draft.partNumber}
          onChange={(e) => set('partNumber', e.target.value)}
          autoFocus
        />
      </label>
      <label className="block">
        <span className={label}>Бренд</span>
        <input
          className={input}
          value={draft.brand}
          onChange={(e) => set('brand', e.target.value)}
        />
      </label>
      <label className="block">
        <span className={label}>Наименование (сегмент)</span>
        <input
          className={input}
          placeholder="пусто — определим сами"
          value={draft.category}
          onChange={(e) => set('category', e.target.value)}
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className={label}>Кол-во *</span>
          <input
            className={input}
            inputMode="decimal"
            value={draft.quantity}
            onChange={(e) => set('quantity', e.target.value)}
          />
        </label>
        <label className="block">
          <span className={label}>Ед.</span>
          <input
            className={input}
            value={draft.unit}
            onChange={(e) => set('unit', e.target.value)}
          />
        </label>
      </div>
      <label className="block">
        <span className={label}>Цена клиенту за ед., ₽ *</span>
        <input
          className={input}
          inputMode="decimal"
          value={draft.price}
          onChange={(e) => set('price', e.target.value)}
        />
      </label>
      <label className="block sm:col-span-2">
        <span className={label}>Матрица</span>
        <select
          className={input}
          value={draft.matrix}
          onChange={(e) => set('matrix', e.target.value as NewPositionDraft['matrix'])}
        >
          <option value="">— не указывать —</option>
          {MATRIX_LABELS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>
      {draft.matrix && (
        <>
          <label className="block">
            <span className={label}>Поставщик</span>
            <input
              className={input}
              placeholder="пусто — «Цена менеджера»"
              value={draft.supplierName}
              onChange={(e) => set('supplierName', e.target.value)}
            />
          </label>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <label className="block">
              <span className={label}>Себестоимость за ед.</span>
              <input
                className={input}
                inputMode="decimal"
                value={draft.costPrice}
                onChange={(e) => set('costPrice', e.target.value)}
              />
            </label>
            <label className="block">
              <span className={label}>Валюта</span>
              <select
                className={input}
                value={draft.costCurrency}
                onChange={(e) =>
                  set('costCurrency', e.target.value as NewPositionDraft['costCurrency'])
                }
              >
                {(['RUB', 'USD', 'EUR', 'CNY'] as const).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}
    </div>
  )
}
